import React, { useState } from 'react';
import {Card, Tabs, Row, Col, Statistic, Alert, Button, Space, Typography} from 'antd';
import {
  MailOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  MonitorOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { PendingMessagesTab } from '../components/response-monitoring/PendingMessagesTab';
import { MonitoringHistoryTab } from '../components/response-monitoring/MonitoringHistoryTab';
import {
  useGetResponseMonitoringStatisticsQuery,
  useTriggerResponseMonitoringCheckMutation,
  useTriggerResponseMonitoringMessageGenerationMutation
} from '../../services/nestjs-base-api';

const { TabPane } = Tabs;
const { Title } = Typography;

export const ResponseMonitoringPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');

  const { data: statistics, isLoading: statsLoading } = useGetResponseMonitoringStatisticsQuery({
    jwtToken: user?.jwtToken || '',
  });

  const [triggerCheck, { isLoading: triggerLoading }] = useTriggerResponseMonitoringCheckMutation();
  const [triggerMessageGeneration, { isLoading: messageGenerationLoading }] = useTriggerResponseMonitoringMessageGenerationMutation();

  const handleTriggerManualCheck = async () => {
    try {
      await triggerCheck({
        jwtToken: user?.jwtToken || '',
      }).unwrap();
      // Show success message or refetch data
    } catch (error) {
      console.error('Failed to trigger manual check:', error);
    }
  };

  const handleRetryFailedMessages = async () => {
    try {
      await triggerMessageGeneration({
        jwtToken: user?.jwtToken || '',
      }).unwrap();
      // Success - data will automatically refetch due to invalidatesTags
    } catch (error) {
      console.error('Failed to retry message generation:', error);
    }
  };

  const renderStatistics = () => {
    if (statsLoading || !statistics) {
      return null;
    }

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Checks"
              value={statistics.totalChecks}
              prefix={<MailOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Critical Issues"
              value={statistics.criticalCount}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Warnings"
              value={statistics.warningCount}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Acceptable"
              value={statistics.acceptableCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Failed Messages"
              value={statistics.checksWithoutMessages || 0}
              valueStyle={{ color: (statistics.checksWithoutMessages || 0) > 0 ? '#cf1322' : '#52c41a' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{margin: 0}}>
            <MonitorOutlined style={{ marginRight: '8px' }} />
            Response Monitoring
          </Title>
          <Space>
            {user?.roles?.includes('SUPER_ADMIN') && (
              <>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={triggerLoading}
                  onClick={handleTriggerManualCheck}
                >
                  Trigger Manual Check
                </Button>
                {statistics && statistics.checksWithoutMessages > 0 && (
                  <Button
                    type="default"
                    icon={<ReloadOutlined />}
                    loading={messageGenerationLoading}
                    onClick={handleRetryFailedMessages}
                  >
                    Retry Failed Messages ({statistics.checksWithoutMessages})
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>

        <Alert
          message="Response Monitoring System"
          description="Monitor survey response rates across all brands and receive AI-generated notifications when response rates fall below acceptable thresholds."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: 16 }}
        />
      </div>

      {renderStatistics()}

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
        >
          <TabPane
            tab={
              <span>
                <MailOutlined style={{ marginRight: 8 }} />
                Pending Messages
              </span>
            }
            key="pending"
          >
            <PendingMessagesTab />
          </TabPane>

          <TabPane
            tab={
              <span>
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                History & Analytics
              </span>
            }
            key="history"
          >
            <MonitoringHistoryTab />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};